/**
 * The multi-select combobox trigger — same shell as SearchableSelect's, so a
 * filter bar mixing the two reads as one row of controls.
 * For Media Devoted | Created By James Alexandrov
 */

import { forwardRef, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { SearchableSelectOption } from '../SearchableSelect';

/** One picked value reads as itself; several collapse to a count, so the
 *  trigger never grows with the selection. */
export const triggerLabel = (
  picked: SearchableSelectOption<string>[],
  placeholder: string,
  noun?: string,
): ReactNode => {
  if (picked.length === 0) return placeholder;
  if (picked.length === 1) return picked[0].optionContent ?? picked[0].label;
  return `${picked.length} ${noun ?? 'selected'}`;
};

interface Props {
  picked: SearchableSelectOption<string>[];
  placeholder: string;
  summaryNoun?: string;
  open: boolean;
  disabled?: boolean;
  className?: string;
}

/** forwardRef + the spread: Radix's Popover.Trigger `asChild` passes its own
 *  ref and handlers down to whatever it wraps. */
export const MultiSelectTrigger = forwardRef<HTMLButtonElement, Props>(
  ({ picked, placeholder, summaryNoun, open, disabled, className, ...rest }, ref) => (
    <button
      {...rest}
      ref={ref}
      type="button"
      disabled={disabled}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-label={placeholder}
      className={cn(
        'flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-input bg-background px-3 text-left text-sm',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        picked.length === 0 && 'text-muted-foreground',
        className,
      )}
    >
      <span className="flex min-w-0 items-center truncate">
        {triggerLabel(picked, placeholder, summaryNoun)}
      </span>
      <ChevronDown
        className={cn(
          'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
          open && 'rotate-180',
        )}
      />
    </button>
  ),
);

MultiSelectTrigger.displayName = 'MultiSelectTrigger';

export default MultiSelectTrigger;
